/**
 * dependentsForDropEdit — derive the list of dependent objects that would
 * break (or whose semantics would change) if a given drop edit committed,
 * given the always-on `DependencyAnnotations` payload from Slice H2.
 *
 * Pure function — no IPC, no side-effects. The commit pipeline consults
 * this synchronously when the blast-radius gate fires, so the dependents
 * surface in the modal matches what the sidebar already showed the user.
 *
 * Granularity:
 *   - drop-column: filter `incoming` by `columnName` match (case-insensitive
 *     on the per-engine attributed value). When the engine's dependency
 *     service does not attribute (mssql), every incoming dependent surfaces
 *     — surfacing too many is safer than surfacing zero, and the introspection
 *     limit is documented in the panel copy.
 *   - drop-table / drop-view / drop-materialized-view / drop-sequence /
 *     drop-type / drop-schema / drop-trigger / drop-routine / drop-package /
 *     drop-database: every `incoming` dependent (the whole object goes away).
 *   - drop-primary-key / drop-foreign-key / drop-unique-constraint /
 *     drop-check-constraint / drop-named-default / drop-index /
 *     drop-partition / drop-tablespace / drop-filegroup / drop-login /
 *     drop-user / drop-role: every `incoming` dependent. The introspection
 *     fallback can't pinpoint per-constraint dependents, so we surface the
 *     full incoming list and let the user judge.
 *
 * Slice H3.
 */
import type { DdlEdit } from '../../../sql-resolver/types';
import type {
  DependencyAnnotations,
  DependencyItem,
} from '../dependencies/types';

export function dependentsForDropEdit(
  edit: DdlEdit,
  annotations: DependencyAnnotations,
): readonly DependencyItem[] {
  if (edit.op === 'drop-column') {
    return filterIncomingByColumn(annotations, edit.columnName);
  }
  // Every other drop op surfaces the full incoming dependents list. The
  // per-column granularity is the only case the introspection fallback
  // can attribute today.
  return annotations.incoming;
}

function filterIncomingByColumn(
  annotations: DependencyAnnotations,
  columnName: string,
): readonly DependencyItem[] {
  const target = columnName.toLowerCase();
  // Look up the column-specific annotation; if the per-engine service did
  // attribute, the FK + view lists are populated and we use them. Otherwise
  // we fall back to the full incoming list (safer-by-default — surfacing
  // every dependent is not wrong, just over-inclusive).
  const annotation = lookupColumnAnnotation(annotations, target);
  if (annotation) {
    const merged: DependencyItem[] = [];
    for (const item of annotation.incomingFkSources) merged.push(item);
    for (const item of annotation.viewProjections) merged.push(item);
    if (merged.length > 0) return merged;
  }
  // Engine didn't attribute — surface the full incoming list as a
  // conservative fallback. Documented in the panel copy + the slice PRD.
  return annotations.incoming;
}

function lookupColumnAnnotation(
  annotations: DependencyAnnotations,
  target: string,
) {
  for (const [key, value] of annotations.perColumn) {
    if (key.toLowerCase() === target) return value;
  }
  return null;
}
